import { Box, Typography, IconButton, useTheme, useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "./TestimonialSection.scss";
import TestimonialCard from "./TestimonialCard";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getPreneedPlannerTestimonials } from "../../../api/preneed-planners.api";
import { dummyTestimonials } from "../data";

const CARD_WIDTH = 360;
const GAP = 24;

interface TestimonialSectionProps {
  partnerId: number;
}

const TestimonialSection: React.FC<TestimonialSectionProps> = ({
  partnerId,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [testimonials, setTestimonials] = useState<any[]>([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const cardWidth = isMobile ? 300 : 360;
  const gap = 24;
  const visibleCards = isMobile ? 1.1 : isTablet ? 2.1 : 2.5;
  const totalCards = testimonials.length;
  const maxScrollIndex = Math.max(0, Math.ceil(totalCards - visibleCards));

  const handleScroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;

    const newIndex =
      direction === "right"
        ? Math.min(scrollIndex + 1, maxScrollIndex)
        : Math.max(scrollIndex - 1, 0);

    const scrollAmount = (cardWidth + gap) * newIndex;
    scrollRef.current.scrollTo({
      left: scrollAmount,
      behavior: "smooth",
    });

    setScrollIndex(newIndex);
  };

  // Fetch testimonials for the given partnerId
  const fetchPreneedPlannersTestimonials = async () => {
    try {
      const response = await getPreneedPlannerTestimonials(partnerId);

      if (response?.data?.success) {
        const list = response.data.data.data;

        setTestimonials(list.length > 0 ? list : dummyTestimonials);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  useEffect(() => {
    if (partnerId) fetchPreneedPlannersTestimonials();
  }, [partnerId]);

  return (
    <Box className="testimonial-section">
      {/* Subtitle */}
      <Typography className="testimonial-section__subtitle">
        <span className="testimonial-section__subtitle--light">Some</span>{" "}
        <span className="testimonial-section__subtitle--bold">
          Testimonials
        </span>
      </Typography>

      {/* Main Title */}
      <Typography className="testimonial-section__title">
        What Families Say About Us
      </Typography>

      {/* if no testimonials */}
      {testimonials.length === 0 ? (
        <Typography className="testimonial-section__no-data">
          No testimonials available yet.
        </Typography>
      ) : (
        <Box className="testimonial-section__carousel-wrapper">
          {/* Left Arrow */}
          <IconButton
            className="testimonial-section__arrow testimonial-section__arrow--left"
            onClick={() => handleScroll("left")}
            disabled={scrollIndex === 0}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          {/* Scrollable Track */}
          <Box className="testimonial-section__carousel" ref={scrollRef}>
            {testimonials.map((review: any, i: number) => (
              <Box key={i} className="testimonial-section__card-wrapper">
                <TestimonialCard
                  firstName={review.user?.first_name || "Unknown User"}
                  lastName={review.user?.last_name || ""}
                  username={review.user?.first_name?.toLowerCase() || "user"}
                  message={review.description}
                  rating={review.rating}
                />
              </Box>
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            className="testimonial-section__arrow testimonial-section__arrow--right"
            onClick={() => handleScroll("right")}
            disabled={scrollIndex >= maxScrollIndex}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default TestimonialSection;
